/**
 * @Description 地址组件：详细地址+经纬度+地图商圈+经营门店
 *
 *   有商圈匹配字段，展示CircleAddress（可查看经纬度，并支持设置经纬度）
 *   默认情况下展示NormalAddress
 *
 *
 *   textValue 数据例
 *
 * {"address":"浙江省杭州市拱墅区上塘街道上塘高架水晶城购物中心","longitude":120.151309,"latitude":30.303581,"poiId":null,"poiName":"浙江省杭州市拱墅区上塘街道上塘高架水晶城购物中心","isCluster":true}
 *
 * 详情页回显时isCluster为true则显示为带经纬度和选址地图商圈信息的样式
 */

import { FC, useContext, useEffect, useState } from 'react';
import NormalAddress from './NormalAddress';
import CircleAddress from './CircleAddress';
// import { useSelector } from 'react-redux';
import { isArray, isNotEmpty } from '@lhb/func';
import { ControlType, formDataComsToTiled } from '../../config';
import ChancePointCircleAddress from './ChancePointCircleAddress';
import UserInfoContext from '@/layout/context';

export interface AddressChangeParams {
  isInitChange?: boolean; // true编辑表单回显时，false手动变更地址
  lng: any;
  lat: any;
  cityName: any;
  poiId: any;
  name: any;
  identification: any;
  isCluster?: boolean; // 是否展示选址地图商圈（展示为CircleAddress时提交的textValue中isCluster为true）
};

const Address: FC<{
  formItemData,
  form,
  wholeFormData,
  disabled,
  required,
  /** 详细地址的特殊赋值逻辑 */
  addressChange,
  /** 是否有区域保护 */
  isAreaProtect,
  /** 地图移动事件 */
  mapMoveChange,
  /** 地图缩放事件 */
  mapZoomChange,
  /** 是否机会点 */
  isChancepoint,
  /** 是否编辑过机会点名称 */
  isEditedBasicChancePointName,
  updateCompValue?,
  /** 拓店任务商圈id，如果传入了拓店商圈，则绘制拓店商圈，且初始化时不绘制中心点及保护区 */
  expansionTaskClusterId,
}> = ({
  formItemData,
  form,
  wholeFormData,
  disabled,
  required,
  addressChange, // 详细地址的特殊赋值逻辑
  isAreaProtect, // 是否有区域保护
  mapMoveChange, // 地图移动事件
  mapZoomChange, // 地图缩放事件
  isChancepoint, // 是否机会点
  isEditedBasicChancePointName, // 是否编辑过机会点名称
  updateCompValue,
  expansionTaskClusterId,
}) => {
  // const tenantCheck = useSelector((state: any) => state.common.tenantCheck);
  const [isCircleTask, setIsCircleTask] = useState<any>(null); // 有确定值之前为null，不展示地址组件
  const { mapConfig } = useContext(UserInfoContext);
  const addressDisabled = typeof isAreaProtect === 'boolean' ? (isAreaProtect || disabled) : disabled;


  // useEffect(() => {
  //   if (!isNotEmpty(tenantCheck.isCircleTask)) return;

  //   if (!isArray(wholeFormData.propertyGroupVOList)) {
  //     setIsCircleTask(tenantCheck.isCircleTask);
  //     return;
  //   }

  //   const allComps: any = [];
  //   formDataComsToTiled(wholeFormData.propertyGroupVOList, allComps); // 将组件对象平铺到数组中
  //   // 是否有商圈匹配字段
  //   const hasBusinessInfoProperty = !!allComps.find((item: any) => item.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value);

  //   // 配置了选址地图拓店任务模板，且有商圈匹配字段
  //   setIsCircleTask(tenantCheck.isCircleTask && hasBusinessInfoProperty);
  // }, [tenantCheck]);

  useEffect(() => {
    if (!isArray(wholeFormData.propertyGroupVOList)) {
      setIsCircleTask(false);
      return;
    }

    const allComps: any = [];
    formDataComsToTiled(wholeFormData.propertyGroupVOList, allComps); // 将组件对象平铺到数组中
    // 是否有商圈匹配字段
    const hasBusinessInfoProperty = !!allComps.find((item: any) => item.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value);

    // 配置了选址地图拓店任务模板，且有商圈匹配字段
    setIsCircleTask(hasBusinessInfoProperty);
  }, [wholeFormData]);

  return (
    <>
      {/* 创建机会点同时配置匹配商圈显示此模块 */}
      { isCircleTask && isChancepoint && <ChancePointCircleAddress
        formItemData={formItemData}
        form={form}
        wholeFormData={wholeFormData}
        // 如果传了isAreaProtect（即isAreaProtect是个布尔值），那么有区域保护则禁用,没有区域保护则按照disabled
        disabled={addressDisabled}
        required={required}
        addressChange={addressChange}
        isAreaProtect={isAreaProtect}
        mapMoveChange={mapMoveChange}
        mapZoomChange={mapZoomChange}
        isEditedBasicChancePointName={isEditedBasicChancePointName}
        updateCompValue={updateCompValue}
        mapConfig={mapConfig}
        expansionTaskClusterId={expansionTaskClusterId}
      /> }
      {/* 非创建机会点同时配置匹配商圈显示此模块 */}
      { isCircleTask && !isChancepoint && <CircleAddress
        formItemData={formItemData}
        form={form}
        wholeFormData={wholeFormData}
        // 如果传了isAreaProtect（即isAreaProtect是个布尔值），那么有区域保护则禁用,没有区域保护则按照disabled
        disabled={addressDisabled}
        required={required}
        addressChange={addressChange}
        isAreaProtect={isAreaProtect}
        mapMoveChange={mapMoveChange}
        mapZoomChange={mapZoomChange}
        mapConfig={mapConfig}
      /> }
      {/* 非未配置匹配商圈显示此模块
      isNotEmpty(isCircleTask) 应该没有意义，isCircleTask 走了 useEffect 应该是否为 false。
      所以这里是当 isCircleTask 为 false 时，展示的组件
      */}
      { !isCircleTask && isNotEmpty(isCircleTask) && <NormalAddress
        formItemData={formItemData}
        form={form}
        disabled={addressDisabled}
        required={required}
        addressChange={addressChange}
        mapMoveChange={mapMoveChange}
        mapZoomChange={mapZoomChange}
        mapConfig={mapConfig}
      /> }
    </>
  );
};

export default Address;
